import { Injectable, signal } from '@angular/core';

export type NotificationType = 'error' | 'info' | 'success' | 'warning';

export interface AppNotification {
  id: number;
  type: NotificationType;
  title: string;
  detail: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly _notifications = signal<AppNotification[]>([]);
  readonly notifications = this._notifications.asReadonly();
  private nextId = 1;

  /**
   * Shows a success toast notification.
   * @param title Short notification heading.
   * @param detail Optional notification detail.
   * @param duration Duration in milliseconds before the toast is dismissed.
   */
  showSuccess(title: string, detail?: string, duration = 4000): void {
    this.show('success', title, duration, detail);
  }

  /**
   * Shows a warning toast notification.
   * @param title Short notification heading.
   * @param detail Optional notification detail.
   * @param duration Duration in milliseconds before the toast is dismissed.
   */
  showWarning(title: string, detail?: string, duration = 5000): void {
    this.show('warning', title, duration, detail);
  }

  /**
   * Shows an error toast notification.
   * @param title Short notification heading.
   * @param detail Optional notification detail.
   * @param duration Duration in milliseconds before the toast is dismissed.
   */
  showError(title: string, detail?: string, duration = 6000): void {
    this.show('error', title, duration, detail);
  }

  /**
   * Shows an informational toast notification.
   * @param title Short notification heading.
   * @param detail Optional notification detail.
   * @param duration Duration in milliseconds before the toast is dismissed.
   */
  showInfo(title: string, detail?: string, duration = 4000): void {
    this.show('info', title, duration, detail);
  }

  /**
   * Removes one notification from the toaster.
   * @param id Identifier of the notification to dismiss.
   */
  dismiss(id: number): void {
    this._notifications.update((notifications) => notifications.filter((notification) => notification.id !== id));
  }

  /**
   * Clears every notification from the toaster.
   */
  clearAll(): void {
    this._notifications.set([]);
  }

  private show(type: NotificationType, title: string, duration: number, detail = ''): void {
    const notification: AppNotification = {
      id: this.nextId,
      type,
      title,
      detail,
    };

    this.nextId += 1;
    this._notifications.update((notifications) => [...notifications, notification]);
    window.setTimeout(() => this.dismiss(notification.id), duration);
  }
}
